import { useParams } from "react-router";
import widgets from "./widgets";
import { useTaskForm } from "./useTaskForm";
import taskTypes, { TaskFormTypeNamesType, TaskFormTypes } from "./types";
import { useOnline } from "../../core/hooks/useOnline";
import { Trans, useTranslation } from "@coworker/locales";
import { PleaseFillIn } from "../Reusable/PleaseFillIn";
import FullScreenPopup from "@coworker/apprestructured/src/layout/components/FullScreenPopup/FullScreenPopup";
import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import { LoadingImage, Task } from "@coworker/types/lib/tasks/base";
import { useMemo } from "react";
import Linkbar from "@coworker/apprestructured/src/shared/components/Linkbar/Linkbar";
import useFixaNavigate from "@coworker/apprestructured/src/shared/wrappers/FixaNavigate/useFixaNavigate";

type FormMode = "create" | "edit" | "complete";

interface FormProps {
  config: TaskFormTypes;
  task_id: string;
  formType: string;
  formMode: FormMode;
  baseTask: Task;
}

function Form({ config, task_id, formType, formMode, baseTask }: FormProps) {
  const { t } = useTranslation();
  const { isOnline } = useOnline();
  const {
    isFormValid,
    createTaskMutation,
    createTaskAction,
    updateTaskAction,
    completeWithChangesAction,
    updateTaskMutation,
    preparedForm,
    form,
    editFields,
    taskPath,
    task,
    images,
  } = useTaskForm({
    config,
    task_id,
    formType,
    formMode,
    baseTask,
  });

  const pickUpAndRefillIdentical = preparedForm.pickup_location_grid_code
    ? preparedForm.pickup_location_grid_code === preparedForm.location_grid_code
    : false;

  const canNotCreate = !isFormValid || !isOnline;
  const getHeaderTitle = () => {
    if (formMode === "complete") {
      return typeof config.header.editTitle === "function"
        ? config.header.editTitle(preparedForm)
        : config.header.editTitle;
    } else if (formMode === "edit") {
      return config.header.editTitle;
    } else if (formMode === "create") {
      return config.header.newTitle;
    }
  };

  const disableCreateButton = () =>
    formType === "mfaq_followup" ||
    formType === "product_quality" ||
    formType === "addon_create"
      ? createTaskMutation.status === "pending"
      : canNotCreate;

  const navigate = useFixaNavigate();

  const getAppBarActionDefinitionByFormMode = (mode: FormMode) => {
    switch (mode) {
      case "create":
        return {
          title: getHeaderTitle(),
        };
      case "edit":
        return {
          title: getHeaderTitle(),
          actions: [
            {
              name: "Cancel",
              icon: <Trans>cancelString</Trans>,
              onClick: () => {
                navigate(-1);
              },
              position: "left" as const,
              testId: "CANCEL",
            },
            {
              name: "Save",
              icon: <Trans>saveString</Trans>,
              onClick: updateTaskAction,
              position: "right" as const,
              disabled: !isOnline || !isFormValid,
              testId: "SAVE",
            },
          ],
        };
      case "complete":
        return {
          title: getHeaderTitle(),
        };
    }
  };

  const linkbarItems = useMemo(() => {
    const items = [
      {
        label: t("TasksString"),
        to: "/tasks",
      },
    ];
    return items;
  }, [t]);

  const linkbar = (
    <Linkbar items={linkbarItems} currentLabel={getHeaderTitle()} />
  );

  return (
    <FullScreenPopup
      linkbar={linkbar}
      appBarConfig={getAppBarActionDefinitionByFormMode(formMode)}
      noPadding
      actionBarContent={
        <>
          {formMode === "create" && (
            <>
              <PleaseFillIn
                show={canNotCreate || pickUpAndRefillIdentical}
                alternateWarningTranslation={t(
                  pickUpAndRefillIdentical
                    ? "refillAndPickupIdenticalString"
                    : "fillInMandatoryFieldsString"
                )}
                centerText={true}
              />
              <FixaButton
                type="primary"
                fluid
                text={
                  isOnline ? (
                    preparedForm.refilled_by_me ? (
                      <Trans>submitCompletedTaskString</Trans>
                    ) : (
                      <Trans>createString</Trans>
                    )
                  ) : (
                    <Trans>goOnlineString</Trans>
                  )
                }
                disabled={disableCreateButton()}
                onClick={() => {
                  if (!canNotCreate && !pickUpAndRefillIdentical) {
                    createTaskAction();
                  }
                }}
                data-testid="confirmButton_q"
                loading={
                  createTaskMutation.status === "pending" ||
                  images?.some((img: LoadingImage) => img.loading)
                }
              />
            </>
          )}
          {formMode === "complete" && (
            <FixaButton
              type="primary"
              fluid
              text={
                isOnline ? (
                  preparedForm.pick_quantity === 0 ||
                  preparedForm.refilled_quantity === 0 ? (
                    <Trans>closeString</Trans>
                  ) : (
                    <Trans>completeString</Trans>
                  )
                ) : (
                  <>
                    <Trans>goOnlineString</Trans> {<div>one</div>}
                  </>
                )
              }
              loading={updateTaskMutation.status === "pending"}
              disabled={
                !isFormValid ||
                !isOnline ||
                updateTaskMutation.status === "pending"
              }
              onClick={completeWithChangesAction}
              data-testid="confirmButton"
            />
          )}
        </>
      }
    >
      {config.fields.map((field, id) => {
        if (field.hide && field.hide(task_id, preparedForm)) {
          return null;
        }
        const Widget = widgets[field.type];

        return (
          <Widget
            key={`${field.type}-${id}`}
            value={(field.values ? field.values.in : (id: string) => id)(
              form,
              task
            )}
            onChange={(change: any) =>
              editFields(field.values ? field.values.out(change, task) : change)
            }
            taskPath={taskPath}
            conf={field.conf}
            taskId={task_id}
            formType={formType}
            last={id === config.fields.length - 1}
            disabled={
              field.disabled
                ? field.disabled({ isEdit: formMode === "edit" })
                : false
            }
            additionalSubTitle={<PleaseFillIn show />}
          />
        );
      })}
    </FullScreenPopup>
  );
}

export function generateTaskForm(type: TaskFormTypeNamesType, mode: FormMode) {
  return (props: Partial<FormProps>) => {
    const { id } = useParams();
    return (
      <Form
        formMode={mode}
        formType={type}
        // @ts-expect-error - Getting a real comment in COAPP-12328 (REDACTED_NAME)
        config={taskTypes[type]}
        task_id={id || ""}
        {...props}
      />
    );
  };
}
